import Vue from "vue";

import debounce from "lodash/debounce";

// import { events } from "@nuskin/ns-util";
import { ShopContextService } from "@nuskin/ns-shop";

import { isNullOrEmpty } from "@nuskin/ns-common-lib";
import { ProductDataService } from "@nuskin/ns-product";
import { Product, PriceType } from "@nuskin/ns-product-lib";

class ProductDataBatch {
  constructor(id, skus) {
    this.id = id;
    this.skus = skus;
    this.status = "ready";
    this.attempt = 1;
  }
}

let NsProductDataService = Vue.prototype.$NsProductDataService;
if (!NsProductDataService) {
  const newEl = document.createElement("div");
  newEl.id = "ns-product-data-service";
  const serviceEl = document.body.appendChild(newEl);

  NsProductDataService = new Vue({
    name: "NsProductDataService",
    data() {
      return {
        /**
         * Controls the amount of skus attempted to fit
         * per batch to send to the Product Data lambda
         */
        batchQuantity: 10,

        /**
         * The list of skus watched to create batches from
         */
        skus: [],

        /**
         * The list of batches that have been created and their current state
         */
        batches: [],

        /**
         * The list of products received and their current state
         */
        products: [],

        /**
         * The max number of attempts we will retry a failed batch
         */
        maxAttempts: 2
      };
    },
    computed: {
      marketConfig() {
        return this.$NsProductAppService.marketConfig;
      },
      isLoggedIn() {
        return this.$NsProductUserService.isLoggedIn;
      },
      user() {
        return this.$NsProductUserService.user;
      },
      userId() {
        return this.$NsProductUserService.userId;
      },
      isWholesale() {
        return this.$NsProductUserService.isWholesale;
      }
    },
    watch: {
      /**
       * Watches this.skus to create batches from
       * @param {string[]} skus
       */
      skus(skus) {
        if (
          !this.$NsProductFeatureFlagsService.useProductData ||
          !skus.length
        ) {
          return;
        }
        this.createBatchesForSkus(skus);
      },

      /**
       * Watches this.batches to send each batch to Product Data lambda
       * @param {ProductDataBatch[]} batches
       */
      batches(batches) {
        for (const batch of batches.filter(
          batch =>
            batch.status === "ready" ||
            (batch.status === "failed" && batch.attempt <= this.maxAttempts)
        )) {
          this.sendBatchToProductDataLambda(batch);
        }
      },

      /**
       * Watches this.userId to set pricing data after logging in or out
       */
      userId() {
        this.updateProductPricing();
      }
    },
    mounted() {
      this.addListeners();
      this.setProductDataFromSession();
    },
    destroyed() {
      this.removeListeners();
    },
    methods: {
      /**
       * Checks a sku or list of skus if already processed.
       * @param {string | string[]} value
       * @param {boolean} includeFailed
       */
      check(value, includeFailed = false) {
        value = Array.isArray(value) ? value : [value];
        const retrievedSkus = this.products.map(p => p.sku);
        return value
          .filter(this.cleanSkus)
          .every(
            sku =>
              retrievedSkus.includes(sku) ||
              this.batches.some(
                batch =>
                  (includeFailed ||
                    ["ready", "sent", "success"].includes(batch.status)) &&
                  batch.skus.includes(sku)
              )
          );
      },

      /**
       * Queue a sku or list of skus to be batched.
       * Avoid manipulating this.skus manually from external locations.
       * @param {string | string[]} value
       */
      queue(value) {
        if (Array.isArray(value)) {
          this.skus.push(...value);
        } else {
          this.skus.push(value);
        }
      },

      /**
       * Find a product if it has already been returned by the Product Data lambda and stored here
       * @param {string} sku
       */
      findProduct(sku, top = true) {
        const products = this.findProducts([sku], top);
        return products.length ? products[0] : null;
      },

      /**
       * Find products if they have already been returned by the Product Data lambda and stored here
       * @param {string[]} skus
       */
      findProducts(skus, top = true) {
        const products = [];
        for (const sku of skus) {
          if (!sku) {
            continue;
          }
          const product = this.products.find(
            p => p.sku === sku || p.variantSkus().includes(sku)
          );
          if (product) {
            if (product.sku === sku || top) {
              products.push(product);
            } else {
              const variant = product.variants[sku];
              products.push(variant);
            }
          }
        }
        return products;
      },

      /**
       * Gets the price type for price display of the current user.
       */
      getPriceType() {
        if (this.isWholesale) {
          return PriceType.WWHL;
        }
        return this.isLoggedIn
          ? ShopContextService.getPriceType(false, this.user)
          : PriceType.WRTL;
      },

      /**
       * Gets the ADR price type for ADR price display of the current user.
       */
      getAdrPriceType() {
        return ShopContextService.getPriceType(true, this.user);
      },

      /**
       * Sends a batch to the Product Data lambda.
       * @param {ProductDataBatch} batch
       */
      async sendBatchToProductDataLambda(batch) {
        if (
          !batch ||
          !batch.skus.length ||
          ["sent", "success"].includes(batch.status) ||
          (batch.status === "failed" && batch.attempt > this.maxAttempts)
        ) {
          return;
        }

        let productData = undefined;
        try {
          batch.status = "sent";
          productData = await ProductDataService.getProductData(batch.skus);
          batch.status = "success";
        } catch (error) {
          batch.status = "failed";
          batch.attempt++;
          console.error(
            `Error sending batch ${
              batch.id
            } to ProductData lambda. Skus requested: ${batch.skus.join(
              ", "
            )}. ${error}`
          );
          return;
        }

        // product data response has products
        if (productData.count && productData.products.length) {
          const productsToSaveToSession = [];
          for (const product of productData.products) {
            // do a last quick check to make sure we don't add duplicate products
            if (!this.findProduct(product.sku)) {
              product.setPriceAndPvFromType(this.getPriceType());
              this.products.push(product);
              this.$emit("product-changed", product);
              productsToSaveToSession.push(product);
            }
          }
          this.saveProductsToSession(productsToSaveToSession);
        }
      },

      /**
       * Updates product pricing
       */
      updateProductPricing() {
        for (const product of this.products) {
          product.setPriceAndPvFromType(this.getPriceType());
          this.$emit("product-changed", product);
        }
      },

      /**
       * Filters invalid and processed skus
       * @param {string[]} skus
       * @param {boolean} includeFailed
       */
      filterSkus(skus, includeFailed = false) {
        // filter out empty, non-unique, already found, and already batched skus
        const filteredSkus = skus
          .filter(this.cleanSkus)
          .filter(sku => !this.check(sku, includeFailed));
        return filteredSkus;
      },

      cleanSkus(value, index, self) {
        return !!value && self.indexOf(value) === index;
      },

      /**
       * Creates new batches for skus passed
       * @param {string[]} skus
       */
      createBatchesForSkus: debounce(function(skus) {
        // only create batches for skus we haven't handled before
        skus = this.filterSkus(skus, true);
        if (!skus.length) {
          this.skus = this.filterSkus(this.skus, true);
          return;
        }

        const batchId = this.getLastBatchId() + 1;

        const batchSkus = skus.slice(0, this.batchQuantity);
        skus = skus.slice(this.batchQuantity + 1);

        const batch = new ProductDataBatch(batchId, batchSkus);
        this.batches.push(batch);

        this.createBatchesForSkus(skus);
      }, 250),

      /**
       * Gets the id for the last batch created
       */
      getLastBatchId() {
        const batchIds = this.batches.map(batch => batch.id);
        return Math.max(0, ...batchIds);
      },

      /**
       * Gets the last batch created
       */
      getLastBatch() {
        const lastBatchId = this.getLastBatchId();
        return this.batches.find(batch => batch.id === lastBatchId);
      },

      /**
       * Handle product status events
       */
      handleProductStatus(productStatusResults) {
        const productsToSaveToSession = [];
        for (const productStatus of productStatusResults) {
          const product = this.findProduct(productStatus.SKU);
          if (product) {
            if (product.isBase() && productStatus.SKU !== product.sku) {
              const variant = product.getVariant(productStatus.SKU);
              if (variant) {
                variant.availableQuantity = productStatus.availableQuantity;
                variant.setMarketAttributes(productStatus);
              }
            } else {
              product.availableQuantity = productStatus.availableQuantity;
              product.setMarketAttributes(productStatus);
            }

            product.addPricingFromStatus(productStatus, this.getPriceType());
            this.$emit("product-changed", product);
            productsToSaveToSession.push(product);
          }
        }
        this.saveProductsToSession(productsToSaveToSession);
      },

      /**
       * Subscribe to events, should only be called when mounted
       */
      addListeners() {
        // WARNING: events.subscribe wasn't always working here
        // listen to product status changes
        // events.subscribe(
        //   events.shop.PRODUCT_STATUS_RESULT,
        //   this.handleProductStatus
        // );
      },

      /**
       * Unsubscribe to events, should only be called when destroyed
       */
      removeListeners() {
        // stop listening to product status changes
        // events.unsubscribe(
        //   events.shop.PRODUCT_STATUS_RESULT,
        //   this.handleProductStatus
        // );
      },

      /**
       * Gets the market config for caching product status data in session storage.
       */
      getProductStatusCachingConfig() {
        const lowThreshold =
          Number(this.marketConfig.productStatusQtyThreshLow) || 100;
        const lowExpiration =
          Number(this.marketConfig.productStatusTtlLow) || 30;

        const mediumThreshold =
          Number(this.marketConfig.productStatusQtyThreshMed) || 500;
        const mediumExpiration =
          Number(this.marketConfig.productStatusTtlMed) || 120;

        const highThreshold =
          Number(this.marketConfig.productStatusQtyThreshHigh) || 1000;
        const highExpiration =
          Number(this.marketConfig.productStatusTtlHigh) || 300;

        return {
          low: {
            threshold: lowThreshold,
            expiration: lowExpiration
          },
          medium: {
            threshold: mediumThreshold,
            expiration: mediumExpiration
          },
          high: {
            threshold: highThreshold,
            expiration: highExpiration
          }
        };
      },

      /**
       * Gets the expiration time in seconds for how long to keep product data in cache.
       */
      getAvailableQuantityCacheExpiration(cachingConfig, availableQuantity) {
        const expiration =
          availableQuantity >= cachingConfig.high.threshold
            ? cachingConfig.high.expiration
            : availableQuantity >= cachingConfig.medium.threshold
            ? cachingConfig.medium.expiration
            : availableQuantity >= cachingConfig.low.threshold
            ? cachingConfig.low.expiration
            : 0;
        return expiration;
      },

      /**
       * Gets product data from session storage
       */
      getProductDataFromSession() {
        const locale = this.$NsProductAppService.locale;
        const sessionStorageKey = `productData-${locale}`;
        const productDataJson = sessionStorage.getItem(sessionStorageKey);

        const productData = productDataJson ? JSON.parse(productDataJson) : {};
        productData.batchQuantity =
          productData.batchQuantity || this.batchQuantity;
        productData.products = productData.products || {};

        if (!isNullOrEmpty(productData.products)) {
          const currentDate = new Date();
          let saveProductsToSession = false;

          const productSkus = Object.keys(productData.products);
          for (const productSku of productSkus) {
            const product = productData.products[productSku];
            const expirationDate = Date.parse(product.expirationDate || 0);
            if (expirationDate < currentDate) {
              delete productData.products[productSku];
              saveProductsToSession = true;
            }
          }

          if (saveProductsToSession) {
            sessionStorage.setItem(
              sessionStorageKey,
              JSON.stringify(productData)
            );
          }
        }

        return productData;
      },

      /**
       * Sets product data from session storage
       */
      setProductDataFromSession() {
        const productData = this.getProductDataFromSession();

        this.batchQuantity = productData.batchQuantity;

        const products = Object.values(productData.products);
        this.products = products.map(p => new Product(p));
      },

      /**
       * Saves new products to session storage
       */
      saveProductsToSession(products) {
        // don't save to session storage with invalid data
        if (
          this.$NsProductAppService.loading ||
          this.$NsProductAppService.locale === "undefined_undefined"
        ) {
          return;
        }

        const cachingConfig = this.getProductStatusCachingConfig();
        const productData = this.getProductDataFromSession();

        for (const product of products) {
          let expiration = 0;
          if (product.isBase()) {
            const variantAvailableQuantities = product
              .getVariantsList()
              .map(v => v.availableQuantity);
            const lowestVariantAvailableQuantity = Math.min(
              ...variantAvailableQuantities
            );
            expiration = this.getAvailableQuantityCacheExpiration(
              cachingConfig,
              lowestVariantAvailableQuantity
            );
          } else {
            expiration = this.getAvailableQuantityCacheExpiration(
              cachingConfig,
              product.availableQuantity
            );
          }

          const expirationDate = new Date();
          expirationDate.setSeconds(expirationDate.getSeconds() + expiration);

          productData.products[product.sku] = {
            ...product.toJSON(),
            expirationDate: expirationDate
          };
        }

        const locale = this.$NsProductAppService.locale;
        const sessionStorageKey = `productData-${locale}`;
        sessionStorage.setItem(sessionStorageKey, JSON.stringify(productData));
      }
    }
  }).$mount(serviceEl);

  Vue.prototype.$NsProductDataService = NsProductDataService;
}

export default NsProductDataService;
